import Player, { PlayerConfig, PlayerProps } from '@pansy/react-aliplayer';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import { BET_MARKET } from 'consts/app';
import useThemes from 'hooks/useThemes';
import { GameResponseModel, OddModel } from 'models';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeSelectGameOddsById } from 'redux/gameSlice';
import { validateOdd } from 'utils/betUtils';
import InactiveButton from '../components/InactiveButton';
import OddButton from '../layouts/components/OddButton';
import { platformStates } from 'redux/platformSlice';

interface StreamingGameProps {
  game: GameResponseModel;
}

const StreamingGame: React.FC<StreamingGameProps> = ({ game }) => {
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const isPortrait = window.matchMedia('(orientation: portrait)').matches;

  const selectGameOddsById = useMemo(() => makeSelectGameOddsById(game.id), [game.id]);
  const oddsByGameId = useSelector(selectGameOddsById);

  return (
    <div className=" padding-wrapper stream-game">
      <div className="video-container">
        <div className="video-box">
          {game.streamUrl ? (
            <VideoPlayer game={game} />
          ) : (
            <div className="not-have-stream primary-text-color">Not Available Stream </div>
          )}
        </div>
        <div className="score-board-container">
          <iframe
            allowTransparency
            scrolling="no"
            className="score-board-frame"
            width="100%"
            src={`https://gsm-widgets.betstream.betgenius.com/multisportscoreboard?productName=terabet${
              currentTheme === 'dark' ? '-dark' : ''
            }&fixtureId=${game.id}`}
          />
        </div>
      </div>
      <div className="odd-container">
        <div className="odd-list">
          {!isPortrait && (
            <div className="score-board-container-mobile">
              <iframe
                allowTransparency
                scrolling="no"
                className="score-board-frame"
                width="100%"
                src={`https://gsm-widgets.betstream.betgenius.com/multisportscoreboard?productName=terabet${
                  currentTheme === 'dark' ? '-dark' : ''
                }&fixtureId=${game.id}`}
              />
            </div>
          )}
          {Object.keys(oddsByGameId).length === 0 ? (
            <EmptyTableIcon text={t('game.empty-live-odd')} />
          ) : (
            Object.entries(oddsByGameId).map(([key, value]) => {
              if (BET_MARKET.includes(key)) {
                return (
                  <div className="mt-10">
                    <LiveOddContainer key={key} odds={value} game={game} />
                  </div>
                );
              } else {
                return null;
              }
            })
          )}
        </div>
      </div>
    </div>
  );
};

interface LiveOddContainerProps {
  odds: OddModel[];
  game: GameResponseModel;
}

const LiveOddContainer: React.FC<LiveOddContainerProps> = ({ odds, game }) => {
  return (
    <div className="wager-list">
      <div className="wager-list__item">
        <div className="header">
          <span className="wager__name">{odds[0].marketName}</span>
        </div>
      </div>
      {odds.length >= 2 ? (
        <div className="wager-list__item mt-10 live-wagget__list">
          {odds.map((odd, index) =>
            validateOdd(odd) ? (
              <OddButton
                key={`${odd.id}`}
                odd={odd}
                index={index}
                gameId={game.id}
                homeTeam={game.homeTeam}
                awayTeam={game.awayTeam}
                category={game.sport.group.toLowerCase()}
                homeTeamLogo={game.homeTeamLogo}
                awayTeamLogo={game.awayTeamLogo}
                gameStatus={game.status}
                isLivePage={true}
              />
            ) : (
              <InactiveButton />
            ),
          )}
        </div>
      ) : (
        <div className="wager-list__item mt-10 live-wagget__list">
          <InactiveButton />
          <InactiveButton />
        </div>
      )}
    </div>
  );
};

interface VideoPlayerProps {
  game: GameResponseModel;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ game }) => {
  const ua = navigator.userAgent;
  const isUseHttp = useMemo(() => {
    return /^((?!chrome|android).)*safari/i.test(ua) || /edg/i.test(ua) || /edge/i.test(ua);
  }, []);

  const playerSource = useMemo(() => {
    return isUseHttp ? `${game.streamUrl.replace('artc', 'https')}` : `${game.streamUrl}`;
  }, [game.streamUrl, isUseHttp]);

  const playerOptions: Partial<Omit<PlayerConfig, 'source' | 'isLive'>> = useMemo(() => {
    return {
      format: 'm3u8',
      useHlsPluginForSafari: true,
    };
  }, [isUseHttp]);

  return <Player isLive={true} source={playerSource} options={playerOptions} />;
};

export default StreamingGame;
